import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import { fetchCentreDashboard, fetchMyCentres } from 'api/procurement'
import { orderCentres } from '../constants'
import SopButton from '../components/SopButton'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  stickyHead: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  select: { minWidth: 180 },
  poolCard: { marginBottom: theme.spacing(2) },
  metricRow: { display: 'flex', gap: theme.spacing(4), flexWrap: 'wrap' },
  metric: { display: 'flex', flexDirection: 'column' },
  value: { fontWeight: 700, fontSize: 20 },
  // The one figure that governs what a centre may still spend, so it carries
  // more weight than the budget and spend it is derived from.
  valueHero: { fontWeight: 700, fontSize: 24 },
  poolNote: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 8 },
  cardLabel: { fontWeight: 600 },
  small: { fontSize: 12, color: theme.palette.text.secondary },
  catRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5)
  },
  // Explicit bar colours. MUI's `color` prop only takes primary|secondary, and
  // secondary is a light grey here — so the fill is set directly instead.
  barOk: {
    backgroundColor: '#eceae4',
    '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.primary.main }
  },
  barOver: {
    backgroundColor: '#f6dcdc',
    '& .MuiLinearProgress-bar': { backgroundColor: '#c62828' }
  },
  pill: { fontWeight: 600 }
}))

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

// A category that has spent its budget reads RED — bar and balance — whether or
// not the centre's pool still covers it (user's call, 2026-08-05). The old
// amber-for-covered kept the distinction on screen but buried the signal: the
// bar was `color="secondary"`, and this theme's secondary.main is #E5E5E5, so
// the one card that needed attention was drawn fainter than the healthy ones.
// `covered` still carries its own note, so "over but not blocked" is not lost.
const TONE = {
  ok: { color: '#2e7d32', bar: 'barOk' },
  covered: { color: '#c62828', bar: 'barOver' },
  over: { color: '#c62828', bar: 'barOver' }
}

const ACTIVITY_TABS = [
  { id: 'all', label: 'All Activity' },
  { id: 'Program Material', label: 'Program' },
  { id: 'Consumables', label: 'Consumables' },
  { id: 'Petty Cash', label: 'Petty Cash' },
  { id: 'Repair & Maintenance', label: 'R&M' }
]

// Bar value, or null when there's NO budget and NO spend — render a dash then
// instead of a misleading empty bar. Spend with no budget reads as fully over.
const barInfo = (used, budget) => {
  const b = Number(budget) || 0
  const u = Number(used) || 0
  if (b <= 0 && u <= 0) return null
  if (b <= 0) return 100
  return Math.min(100, (u / b) * 100)
}

// Map a budget-category key to its Center-Team tab id so a card can deep-link.
const CATEGORY_TAB = {
  program_material: 'program-material',
  consumables: 'consumables',
  petty_cash: 'petty-cash',
  repair_maintenance: 'repair-maintenance'
}

const Dashboard = ({ centreId: controlledCentreId, onNavigate } = {}) => {
  const classes = useStyles()
  // Centre supplied by the Center Team layout when controlled (picker hidden).
  const controlled = !!controlledCentreId

  const [centres, setCentres] = React.useState([])
  const [centreId, setCentreId] = React.useState(controlledCentreId || '')
  const [month, setMonth] = React.useState(Dayjs().format('YYYY-MM'))
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [tab, setTab] = React.useState('all')

  const monthDate = `${month}-01`

  React.useEffect(() => {
    if (controlled) {
      setCentreId(controlledCentreId)
      return
    }
    ;(async () => {
      try {
        const res = await fetchMyCentres()
        const list = orderCentres(res?.results || [])
        setCentres(list)
        if (list.length) setCentreId(list[0].uuid)
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to load centres',
          constants.ERROR,
          'Dashboard'
        )
      }
    })()
  }, [controlled, controlledCentreId])

  const load = React.useCallback(async () => {
    if (!centreId) return
    setLoading(true)
    try {
      const res = await fetchCentreDashboard({
        centre: centreId,
        month: monthDate
      })
      setData(res)
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load dashboard',
        constants.ERROR,
        'Dashboard'
      )
    } finally {
      setLoading(false)
    }
  }, [centreId, monthDate])

  React.useEffect(() => {
    load()
  }, [load])

  const pool = data?.pool
  // The activity row whose items are being viewed. Only rows that CARRY items
  // (a program-material order) can open — a petty-cash expense is one line and
  // has nothing to drill into.
  const [openItems, setOpenItems] = React.useState(null)

  const activity = (data?.activity || []).filter(
    (a) => tab === 'all' || a.category === tab
  )

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.toolbar}>
        {!controlled && <Typography variant="h6">Centre Dashboard</Typography>}
        {!controlled && (
          <TextField
            select
            className={classes.select}
            size="small"
            variant="outlined"
            label="Centre"
            value={centreId}
            onChange={(e) => setCentreId(e.target.value)}>
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {data && (
          <Typography variant="body2" color="textSecondary">
            FY {data.fiscalYear}
          </Typography>
        )}
        <SopButton category="dashboard" />
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : !data ? null : (
        <>
          {/* Centre budget pool */}
          <Card className={classes.poolCard} variant="outlined">
            <CardContent>
              <div className={classes.catRow}>
                <Typography className={classes.cardLabel}>
                  Centre Budget
                </Typography>
                <Chip
                  size="small"
                  className={classes.pill}
                  label={pool.over ? 'Over budget' : 'Within budget'}
                  style={{
                    backgroundColor: pool.over ? '#ffebee' : '#e8f5e9',
                    color: pool.over ? '#c62828' : '#2e7d32'
                  }}
                />
              </div>
              <Box my={1}>
                {barInfo(pool.totalUtilised, pool.totalEffective) === null ? (
                  <Typography className={classes.small} style={{ color: '#bbb' }}>
                    — no budget set
                  </Typography>
                ) : (
                  <LinearProgress
                    variant="determinate"
                    className={pool.over ? classes.barOver : classes.barOk}
                    value={barInfo(pool.totalUtilised, pool.totalEffective)}
                  />
                )}
              </Box>
              <div className={classes.metricRow}>
                <div className={classes.metric}>
                  <span className={classes.small}>Budget</span>
                  <span className={classes.value}>
                    {inr(pool.totalEffective)}
                  </span>
                </div>
                <div className={classes.metric}>
                  <span className={classes.small}>Spent</span>
                  <span className={classes.value}>
                    {inr(pool.totalUtilised)}
                  </span>
                </div>
                <div className={classes.metric}>
                  <span className={classes.small}>Left to spend</span>
                  <span
                    className={classes.valueHero}
                    style={{ color: pool.totalBalance < 0 ? '#c62828' : '#2e7d32' }}>
                    {inr(pool.totalBalance)}
                  </span>
                </div>
              </div>
              {/* The cards below are the four differences that make up this
                  number, so say which number is the one that governs. */}
              {pool.totalBalance >= 0 && (
                <Typography className={classes.poolNote}>
                  You can spend the {inr(pool.totalBalance)} on any category,
                  except petty cash, which stops at its own budget.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Per-category cards */}
          <Grid container spacing={2}>
            {data.categories.map((c) => {
              const tone = TONE[c.tone] || TONE.ok
              const tabId = CATEGORY_TAB[c.key]
              const clickable = !!(onNavigate && tabId)
              return (
                <Grid item xs={12} sm={6} md={3} key={c.key}>
                  <Card
                    variant="outlined"
                    onClick={
                      clickable ? () => onNavigate(tabId, monthDate) : undefined
                    }
                    style={clickable ? { cursor: 'pointer' } : undefined}
                    title={clickable ? `Open ${c.name}` : undefined}>
                    <CardContent>
                      <Typography className={classes.cardLabel}>
                        {c.name}
                      </Typography>
                      <Typography variant="h6">{inr(c.base)}</Typography>
                      <Typography className={classes.small}>
                        allowed this month
                      </Typography>
                      <Box my={1}>
                        {barInfo(c.utilised, c.base) === null ? (
                          <Typography
                            className={classes.small}
                            style={{ color: '#bbb' }}>
                            — no budget set
                          </Typography>
                        ) : (
                          <LinearProgress
                            variant="determinate"
                            className={classes[tone.bar]}
                            value={barInfo(c.utilised, c.base)}
                          />
                        )}
                      </Box>
                      <div className={classes.catRow}>
                        <span className={classes.small}>Spent</span>
                        <span>{inr(c.utilised)}</span>
                      </div>
                      {/* "Spare" / "Over" rather than a signed Balance: the
                          word carries the sign, so the four cards read as the
                          plus-and-minus list that sums to the centre's Left to
                          spend above. */}
                      <div className={classes.catRow}>
                        <span className={classes.small}>
                          {c.balance < 0 ? 'Over' : 'Spare'}
                        </span>
                        <span style={{ color: tone.color, fontWeight: 600 }}>
                          {c.balance < 0 ? '−' : '+'}
                          {inr(Math.abs(Number(c.balance) || 0))}
                        </span>
                      </div>
                      {c.tone === 'covered' && (
                        <Typography
                          className={classes.small}
                          style={{ color: '#c62828', marginTop: 4 }}>
                          Covered by the centre's spare
                        </Typography>
                      )}
                      {c.tone === 'over' && (
                        <Typography
                          className={classes.small}
                          style={{ color: '#c62828', marginTop: 4 }}>
                          Over the centre's total budget
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {/* Recent activity */}
          <Box mt={3}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Recent Activity
            </Typography>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 8 }}>
              {ACTIVITY_TABS.map((t) => (
                <Tab key={t.id} value={t.id} label={t.label} />
              ))}
            </Tabs>
            <Table size="small" className={classes.stickyHead}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activity.map((a) => {
                  const hasItems = !isEmpty(a.lines)
                  return (
                  <TableRow
                    key={a.id}
                    hover
                    onClick={() => hasItems && setOpenItems(a)}
                    style={{ cursor: hasItems ? 'pointer' : 'default' }}
                  >
                    <TableCell>{Dayjs(a.date).format('D MMMM YYYY')}</TableCell>
                    <TableCell>
                      {a.description}
                      {hasItems && (
                        <Typography
                          variant="caption"
                          color="primary"
                          style={{ marginLeft: 8 }}
                        >
                          view items
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={a.category} variant="outlined" />
                    </TableCell>
                    <TableCell
                      align="right"
                      style={{ color: a.received ? '#2e7d32' : 'inherit' }}>
                      {a.received ? '+' : ''}
                      {inr(a.amount)}
                    </TableCell>
                    <TableCell style={{ textTransform: 'lowercase' }}>
                      {a.status || '—'}
                    </TableCell>
                  </TableRow>
                  )
                })}
                {isEmpty(activity) && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="textSecondary">
                        No activity this month.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {/* The items behind an activity row. You used to have to leave the
              dashboard and dig through the category tab to answer "what did we
              actually order?" — the question the row itself provokes. */}
          <Dialog
            open={!!openItems}
            onClose={() => setOpenItems(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              {openItems?.category} — {Dayjs(openItems?.date).format('MMMM YYYY')}
            </DialogTitle>
            <DialogContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    {/* A program-material line carries a catalogue ref; a
                        consumables line carries the unit it is counted in. Same
                        column, honest label. */}
                    <TableCell>
                      {openItems?.category === 'Consumables' ? 'Unit' : 'Ref id'}
                    </TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit cost</TableCell>
                    <TableCell align="right">Line total</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(openItems?.lines || []).map((l, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={i}>
                      <TableCell>{l.item_name}</TableCell>
                      <TableCell>{l.reference_id || '—'}</TableCell>
                      <TableCell align="right">{l.qty}</TableCell>
                      <TableCell align="right">{inr(l.unit_cost)}</TableCell>
                      <TableCell align="right">{inr(l.line_total)}</TableCell>
                      <TableCell>{l.remarks || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Total: {inr(openItems?.amount)}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenItems(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Paper>
  )
}

export default Dashboard
